import { ref } from "vue";
import { AudioProcessor } from "../../utils/AudioProcessor";
import { LocalAudioTrack, LocalTrack } from "livekit-client";
import {
  ensureAudioContextRunning,
  getMicProcessingContext,
  loadAudioModules,
  microphoneAudioContext,
  resumeAudioContext,
} from "../audioCtx";
import { microTrack } from "./state";
import { setSpeak } from "../speakOnMicroOffNotify";
import { isCurrentSession, needSync } from "./pipe";
import type { MediaDesired } from "./state";

function getStoredNoiseSuppression() {
  const inStorage = localStorage.getItem("noiseSuppression");
  if (inStorage === null) return false;
  return inStorage === "1";
}

export const noiseSuppression = ref(getStoredNoiseSuppression());

let currentMicroProcessor: AudioProcessor | null = null;

export function setNoiseSuppression(val: boolean) {
  noiseSuppression.value = val;
  localStorage.setItem("noiseSuppression", val ? "1" : "0");
  needSync();
}

export const currentMicroLevel = ref(0);

export function clearMicroProcessor(processor: AudioProcessor) {
  if (currentMicroProcessor === processor) currentMicroProcessor = null;
  return processor.destroy().catch((e) => {
    console.error("err destroy micro processor", e);
  });
}

export async function applyAudioProcessor(
  track: LocalTrack,
  noiseSuppressionEnabled: boolean,
) {
  const processor = new AudioProcessor(noiseSuppressionEnabled);
  currentMicroProcessor = processor;
  try {
    const micCtx = getMicProcessingContext();
    if (micCtx) (track as LocalAudioTrack).setAudioContext(micCtx);
  } catch (err) {
    console.log(err);
  }
  resumeAudioContext();
  await track.setProcessor(processor);
  if (currentMicroProcessor === processor) {
    // Тумблер мог переключиться, пока навешивался процессор: доводим граф до
    // желаемого состояния сразу, не дожидаясь следующего прохода.
    if (processor.noiseSuppressionEnabled !== noiseSuppressionEnabled) {
      await processor.setNoiseSuppressionEnabled(noiseSuppressionEnabled);
    }
  }
  resumeAudioContext();
  await ensureAudioContextRunning(microphoneAudioContext);
}

/**
 * Реконсиляция AI-шумодава под желаемое состояние. Трек при этом не
 * пересоздаётся: constraints не зависят от тумблера (браузерные NS и AEC
 * включены всегда), меняется только граф микрофонного процессора.
 */
export async function noiseSuppressionTask(desired: MediaDesired) {
  if (!isCurrentSession(desired.generation)) return;
  const track = microTrack.value;
  if (!track) return;
  const processor = track.getProcessor() as AudioProcessor | undefined;
  if (!processor) return;
  if (processor.noiseSuppressionEnabled === desired.noiseSuppression) return;
  await processor.setNoiseSuppressionEnabled(desired.noiseSuppression);
}

const workletNodes = new Map<MediaStreamTrack, AudioWorkletNode>();

export async function setupProcessingTrack(track: MediaStreamTrack) {
  const audioCtx = getMicProcessingContext()
  if (!audioCtx) return;

  const mediaStream = new MediaStream([track]);
  const sourceNode = audioCtx.createMediaStreamSource(mediaStream);

  const destinationNode = audioCtx.createMediaStreamDestination();

  // Модуль регистрируется per-AudioContext (см. loadAudioModules в audioCtx.ts),
  // но страхуемся на случай раннего вызова до router-guard.
  const createVadNode = () =>
    new AudioWorkletNode(audioCtx, "vad-processor", {
      processorOptions: {},
    });
  let workletNode: AudioWorkletNode;
  await loadAudioModules();
  try {
    workletNode = createVadNode();
  } catch {
    const vadProcessorUrl = new URL("../utils/vad-processor.js", import.meta.url)
      .href;
    await audioCtx.audioWorklet.addModule(vadProcessorUrl);
    workletNode = createVadNode();
  }
  workletNodes.set(track, workletNode);

  workletNode.port.onmessage = (event) => {
    const { currentVolume, speak } = event.data;
    currentMicroLevel.value = currentVolume;
    setSpeak(speak)
  };

  sourceNode.connect(workletNode);
  workletNode.connect(destinationNode);
}

export function destroyWorkletNode(track: MediaStreamTrack) {
  const workletNode = workletNodes.get(track);
  if (workletNode) {
    workletNode.port.onmessage = null;
    workletNode.disconnect();
    workletNodes.delete(track);
    currentMicroLevel.value = 0;
  }
}
